import React from 'react'
import { useLocation } from 'react-router'
import { throttle } from '@mobi/utils/functions'
import {
  cleanUpScrollRestoreOverrideData,
  getScrollRestoreOverride,
  hasScrollRestoreOverride,
  setScrollRestoreOverride,
} from '../helpers/scrollRestoreOverride'

/** Manual back navigation scroll restoration for route.
 *  ONLY Use in top level components
 */
export const useScrollRestoreOverride = ({
  hasPageLoaded,
  customScrolledElement,
}: {
  hasPageLoaded: boolean
  customScrolledElement?: React.RefObject<HTMLDivElement>
}) => {
  const location = useLocation()

  React.useEffect(() => cleanUpScrollRestoreOverrideData, [])

  // Set scroll position on mount, and capture position for route key on scroll
  React.useEffect(() => {
    if (!hasPageLoaded) return

    const key = location.key
    const isUsingCustomElement = !!customScrolledElement?.current
    const scrollElement = isUsingCustomElement ? customScrolledElement.current : window

    const top = (hasScrollRestoreOverride(key) && getScrollRestoreOverride(key)) || 0
    scrollElement.scrollTo({ top, behavior: 'instant' })

    const watchScroll = () => {
      const offset = isUsingCustomElement
        ? customScrolledElement.current.scrollTop
        : window.pageYOffset
      setScrollRestoreOverride(key, offset)
    }
    const watchScrollThrottled = throttle(watchScroll, 200)
    scrollElement.addEventListener('scroll', watchScrollThrottled)

    return () => {
      watchScrollThrottled.cancel()
      scrollElement.removeEventListener('scroll', watchScrollThrottled)
    }
  }, [location.key, customScrolledElement, hasPageLoaded])

  return hasScrollRestoreOverride(location.key)
}
